import { HttpStatusCode } from '@angular/common/http';
import { AppLanguage, ISOLanguage } from '../../applanguage/app-language';
import { IPhotoEntityReference } from '../../basic';
import { GsStructBlock } from '../blocks/block';
import { MetaData } from './metadata';

export interface GsStructPage {
  /** The id of the page for the db */
  iD: number;
  /** The name of the page for references into the backend */
  name: string;
  /** The url, to enable routing to this page */
  url: string;
  /** The title of the page to display for example in the tab */
  title: string;
  /** Is this page deleted? */
  deleted: boolean;
  /** The blocks inside this page */
  blocks: GsStructBlock[];
  /** Status code of the request */
  statusCode?: HttpStatusCode;
  /** Page MasterView */
  masterView?: GsStructPageMasterView;
  /** Page MetaData */
  metaData?: MetaData;
  /** Page Picture */
  picture?: IPhotoEntityReference;
  /** Page short description */
  shortDescription?: string;
  /** Page containers */
  container: GsStructPageContainer[];
  /** Language ISO code for the page (e.g., 'de-DE', 'en-US', 'nl-NL', 'de-CH') */
  language?: ISOLanguage;
  /** Available languages for this page as key-value pairs (e.g., { "de-DE": "Deutsch", "en-US": "English" }) */
  languages?: Partial<Record<AppLanguage, string>>;
  /** Indicates if the page is a plugin page for app */
  isPlugin?: boolean;
  /** Indicates if the page is rendered inside an app WebView (unlike `isPlugin`, also true for regular CS4 pages) */
  inApp?: boolean;
}

export interface GsStructPageMasterView {
  /** Should the breadcrumb get shown? */
  showbreadcrumb: boolean;
  /** Margin of the page (button-side) */
  pagemarginbottom: number;
  /** Margin of the page (top-side) */
  pagemargintop: number;
  /** height of the hero-picture */
  pictureheight: number;
  /** Should the hero-picture get shown? */
  showpicture: boolean;
  /** Should the title get shown on the hero-picture? */
  withtitleonpicture: boolean;
  /** Should the title get shown? */
  withtitle: boolean;
  /** Defined the width of the page */
  layout: MasterViewLayout;
  /** Should the short description get shown? */
  showshortdescription: boolean;
  /** The breadcrumb view mode */
  breadcrumb?: BreadcrumbLayout;
  /** Settings for the breadcrumb component */
  breadcrumbSettings?: {
    /** Automatically scroll to the end when breadcrumbs overflow. */
    autoScrollToEnd: boolean;
    /**Separator character or string displayed between breadcrumb items. */
    separator: string;
    /** Whether to show the ellipsis button on small screens when breadcrumbs are collapsed. */
    showEllipsis: boolean;
    /** TODO: The mode in which the breadcrumb should be displayed. */
    displayMode?: BreadcrumbDisplayMode;
  };
}

/**
 * Page layout types
 * Layout = Inhalt auf voller Breite
 * LayoutContent = Inhalt mit Container
 * LayoutEmpty = Inhalt ohne Header, Footer, Container
 */
export type MasterViewLayout = 'LayoutEmpty' | 'LayoutContent' | 'Layout';
/**
 * Breadcrumb layout types
 * Auto = Automatisch je nach MasterView Layout
 * LayoutContent = Immer mit Container
 * Layout = Immer ohne Container
 */
export type BreadcrumbLayout = 'Auto' | 'LayoutContent' | 'Layout';

/**
 * The mode in which the breadcrumb should be displayed.
 */
export enum BreadcrumbDisplayMode {
  /** Wrap the text, not scrollable */
  Wrap = 0,
  /** Only show the last crumb */
  LastOnly = 1,
  /** Show as many as possible, first as "…" */
  Truncate = 2
}

export interface GsStructPageContainer {
  /** Unique identifier of the container */
  containerID: string;
  /** Key for container identification */
  key: string;
  /** Display name of the container */
  name: string;
  /** Image/photo of the container */
  picture: IPhotoEntityReference;
  /** List of container items */
  items: GsStructPageContainerItem[];
}

export interface GsStructPageContainerItem {
  /** Unique identifier of the container item */
  containerItemID: string;
  /** Main title of the item */
  title: string;
  /** Secondary title of the item */
  title2: string;
  /** Description text of the item */
  description: string;
  /** Detailed information of the item */
  details: string;
  /** External URL for the item */
  externalUrl: string;
}
